import { useCallback, useEffect, useMemo, useState } from "react"
import {
  AlertCircle,
  CheckCircle,
  useSnackbar,
  useStyletron,
} from "@mezo-org/mezo-clay"

import {
  Notification,
  selectNotification,
  setNotification,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"

type ConnectionNotificationVariant = "success" | "fail" | "disconnected"

export const useNotification = (
  notificationType: Notification,
  extraCondition?: boolean,
  callback?: () => void,
) => {
  const notification = useDappSelector(selectNotification)

  const [showNotification, setShowNotification] = useState(true)
  const [hideNotification, setHideNotification] = useState(false)

  const notificationCondition = useMemo(
    () => (extraCondition === undefined ? true : extraCondition),
    [extraCondition],
  )

  const dispatch = useDappDispatch()

  useEffect(() => {
    setShowNotification(
      notificationCondition && notification === notificationType,
    )
  }, [notification, notificationCondition, notificationType])

  useEffect(() => {
    if (!showNotification) return () => {}

    const hideTimeout = setTimeout(() => {
      setHideNotification(true)
    }, 9_000)

    const showTimeout = setTimeout(() => {
      if (callback) callback()
      dispatch(setNotification(null))
      setShowNotification(false)
    }, 10_000)

    return () => {
      clearTimeout(hideTimeout)
      clearTimeout(showTimeout)
    }
  }, [showNotification, callback, dispatch])

  return { hideNotification, showNotification }
}

export const useSnackbarNotification = () => {
  const { enqueue } = useSnackbar()
  const [, theme] = useStyletron()

  const showSnackbarNotification = useCallback(
    (
      message: string,
      background: string,
      icon:
        | React.ComponentType<{
            size: number
          }>
        | undefined,
    ) => {
      enqueue({
        message,
        startEnhancer: icon,
        overrides: {
          Root: {
            style: () => ({
              marginTop: "68px",
              marginRight: theme.sizing.scale1200,
              zIndex: 99,
            }),
          },
          Content: {
            style: () => ({
              background,
              borderRadius: theme.borders.radius300,
            }),
          },
        },
      })
    },
    [enqueue, theme],
  )

  return { showSnackbarNotification }
}

export const useWalletConnectionNotification = () => {
  const { showSnackbarNotification } = useSnackbarNotification()
  const [, theme] = useStyletron()

  const showConnectionNotification = useCallback(
    (variant: ConnectionNotificationVariant) => {
      const notificationColor: Record<ConnectionNotificationVariant, string> = {
        success: theme.colors.contentPositive,
        fail: theme.colors.backgroundNegative,
        disconnected: theme.colors.black,
      }

      const notificationText: Record<ConnectionNotificationVariant, string> = {
        success: "Successfully connected.",
        fail: "There was an error. Try again.",
        disconnected: "Disconnected.",
      }

      showSnackbarNotification(
        notificationText[variant],
        notificationColor[variant],
        () => (variant === "fail" ? <AlertCircle /> : <CheckCircle />),
      )
    },
    [showSnackbarNotification, theme],
  )

  return { showConnectionNotification }
}

export const useCodeCopiedNotifcation = () => {
  const { showSnackbarNotification } = useSnackbarNotification()
  const [, theme] = useStyletron()

  const showCodeCopiedNotification = useCallback(() => {
    showSnackbarNotification(
      "Succesfully copied code",
      theme.colors.black,
      () => <CheckCircle />,
    )
  }, [showSnackbarNotification, theme])

  return { showCodeCopiedNotification }
}
